import { resolveSeasonalEventiumIconKey } from "@/event";
import useTileRepository from "@/hooks/useTileRepository";
import { formatNumber } from "@/utils/format";
import { translate } from "@/utils/translate";
import Phaser from "phaser";

function getWidth(
	obj: Phaser.GameObjects.Text | Phaser.GameObjects.BitmapText,
): number {
	return obj.width;
}

// Varianty toastu
export enum TileToastType {
	INCOME = "income",
	EXPLORE = "explore",
	WARNING = "warning",
	SUCCESS = "success",
	REVEAL = "reveal",
	NEUTRAL = "neutral",
	ERROR = "error",
	OFFLINE = "offline",
	PROVISION = "provision",
}

export const spriteKeyToIndex = {
	"icon-coins": 0,
	"icon-food": 1,
	"icon-workforce": 2,
	"icon-gems": 3,
	"icon-lumber": 4,
	"icon-clay": 5,
	"icon-stone": 6,
	"icon-goods": 7,
	"icon-iron": 8,
	"icon-steel": 9,
	"icon-army": 10,
	"icon-superclicker": 11,
	"icon-adskipper": 12,
	"icon-chest": 13,
	"icon-piggybankprogress": 14,
	"icon-rations": 15,
};

export type ToastVariant = TileToastType;

export enum ToastPlacement {
	TOP_LEFT = "top-left",
	TOP_CENTER = "top-center",
	TOP_RIGHT = "top-right",
	BOTTOM_LEFT = "bottom-left",
	BOTTOM_CENTER = "bottom-center",
	BOTTOM_RIGHT = "bottom-right",
}

export interface ToastOptions {
	duration?: number;
	position?: { x: number; y: number };
	placement?: ToastPlacement;
	iconKey?: string;
	messageType?: ToastVariant;
	textTwo?: string;
	randomX?: boolean;
	customMessageTime?: number;
	topCorrection?: number;
	messageNoAnim?: boolean;
	messageScale?: boolean;
	messageNumber?: number;
	ignoreBounds?: boolean;
}

export class ToastMessagePool {
	private pool: ToastMessage[] = [];
	private scene: Phaser.Scene;
	private useBitmapText: boolean;

	constructor(scene: Phaser.Scene, initialSize = 10, useBitmapText = true) {
		this.scene = scene;
		this.useBitmapText = useBitmapText;

		for (let i = 0; i < initialSize; i++) {
			this.pool.push(new ToastMessage(scene, useBitmapText));
		}
	}

	getForVariant(
		variant: ToastVariant,
		maxPoolSize: number = 5,
	): ToastMessage {
		// Nájdeme všetky toasty s daným variantom
		const variantToasts = this.pool.filter(
			t => t.activeVariant === variant,
		);

		if (variantToasts.length > 0) {
			if (variantToasts.length >= maxPoolSize) {
				// Najstarší toast = prvý v poli
				const oldest = variantToasts[0];
				oldest.cancelTween?.();
				oldest.hide();
				this.pool.splice(this.pool.indexOf(oldest), 1);
				this.pool.push(oldest);
				// Vezmeme prvý z recyklovaných
				return oldest;
			} else {
				// Menej ako maxPoolSize → použijeme voľný toast, ak je
				const available = this.pool.find(t => !t.container.visible);
				if (available) return available;
			}
		} else {
			// Žiadny toast s týmto variantom ešte nie je → použijeme voľný toast
			const available = this.pool.find(t => !t.container.visible);
			if (available) return available;
		}

		// Ak sa nenašiel voľný toast → vytvoríme nový
		const newToast = new ToastMessage(this.scene, this.useBitmapText);
		this.pool.push(newToast);
		return newToast;
	}
}

export class ToastMessage {
	scene: Phaser.Scene;
	container: Phaser.GameObjects.Container;
	leftDecor: Phaser.GameObjects.Image;
	rightDecor: Phaser.GameObjects.Image;
	bg: Phaser.GameObjects.Rectangle;
	icon?: Phaser.GameObjects.Image;
	text: Phaser.GameObjects.Text | Phaser.GameObjects.BitmapText;
	secondText?: Phaser.GameObjects.Text | Phaser.GameObjects.BitmapText;
	useBitmapText: boolean;
	tilerepo: ReturnType<typeof useTileRepository>;
	currentTween?: Phaser.Tweens.Tween;
	activeVariant?: ToastVariant;

	constructor(scene: Phaser.Scene, useBitmapText: boolean = true) {
		this.scene = scene;
		this.useBitmapText = useBitmapText;
		this.tilerepo = useTileRepository();

		this.leftDecor = this.scene.add
			.image(0, 0, "toast-end-left")
			.setOrigin(0, 0.5);
		this.rightDecor = this.scene.add
			.image(0, 0, "toast-end-right")
			.setOrigin(1, 0.5);
		this.bg = this.scene.add
			.rectangle(0, 0, 200, this.leftDecor.height, 0x171717, 0.8)
			.setOrigin(0.5);

		this.text = this.useBitmapText
			? this.scene.add
					.bitmapText(0, 0, "texturina", "", 112)
					.setOrigin(0.5)
			: this.scene.add
					.text(0, 0, "", {
						fontSize: "7rem",
						color: "#fff3dd",
						align: "center",
						fontFamily: "Texturina",
					})
					.setOrigin(0.5);

		this.container = this.scene.add.container(0, 0, [
			this.leftDecor,
			this.bg,
			this.rightDecor,
			this.text,
		]);
		this.container.setSize(200, this.leftDecor.height);
		this.leftDecor.x = -this.container.width / 2;
		this.rightDecor.x = this.container.width / 2;
		this.container.setVisible(false);
		this.container.setDepth(3000);
	}

	showToast(
		message: string | number,
		options: ToastOptions,
		cameraZoom: number,
		onDone?: () => void,
	): void {
		const SCALE_COEFFICIENT = 0.15 * window.devicePixelRatio;

		const scaleValue =
			(1 / (cameraZoom ?? 1)) *
			SCALE_COEFFICIENT *
			(options.messageScale ? 1.2 : 1);

		this.setContent(message, options);
		this.activeVariant = options.messageType;
		const { TILE_SIZE } = this.tilerepo;
		const MULTIPLE_REWARD_GAP = 1.6 * scaleValue;

		const OFFSET_BASE = options.topCorrection ?? 240;

		const visualOffset = Math.min(
			OFFSET_BASE,
			OFFSET_BASE / (cameraZoom ?? 1),
		);

		let pos = options.position
			? {
					x:
						options.position.x * TILE_SIZE +
						(options.randomX ? Phaser.Math.Between(-175, 175) : 0),
					y: options.position.y * TILE_SIZE - visualOffset,
				}
			: { x: 0, y: 0 };

		if (
			options.messageNumber &&
			options.messageNumber > 0 &&
			!options.placement
		) {
			pos.y +=
				options.messageNumber *
				this.leftDecor.height *
				MULTIPLE_REWARD_GAP;
		}

		if (options.placement) {
			const cam = this.scene.cameras.main;
			if (options.placement === ToastPlacement.TOP_CENTER) {
				pos = {
					x: cam.worldView.centerX,
					y: cam.worldView.y + cam.worldView.height / 3.5,
				};
				if (options.messageNumber && options.messageNumber > 0) {
					pos.y +=
						options.messageNumber *
						this.leftDecor.height *
						MULTIPLE_REWARD_GAP;
				}
			}

			if (options.placement === ToastPlacement.BOTTOM_CENTER) {
				pos = {
					x: cam.worldView.centerX,
					y: cam.worldView.y + cam.worldView.height * 0.75,
				};
			}
		}

		this.show(pos, cameraZoom, options.messageScale, options.ignoreBounds);

		const duration = options.duration ?? 100;
		this.scene.time.delayedCall(duration, () => {
			this.hideAnimated(
				() => {
					if (onDone) onDone();
				},
				options.customMessageTime ?? 1500,
				options.messageNoAnim,
				scaleValue,
			);
		});
	}

	setBackground(hexColor: number, alpha: number): void {
		this.bg.setFillStyle(hexColor, alpha);
	}

	setContent(message: string | number, options: ToastOptions): void {
		const variant = options.messageType;

		this.text.setText(
			typeof message === "number"
				? `${message >= 0 ? `${variant === TileToastType.OFFLINE ? translate("ui.offline_earnings") + ": " : ""} +` : ""}${formatNumber(+message).replace(/\u00A0/g, " ")}`
				: message,
		);

		switch (variant) {
			case TileToastType.WARNING:
				this.setColor(this.text, 0xff3d3d);
				this.removeIcon();
				this.removeSecondText();
				break;
			case TileToastType.SUCCESS:
				this.setColor(this.text, 0x19b345);
				this.removeIcon();
				this.removeSecondText();
				break;
			case TileToastType.NEUTRAL:
				this.setColor(this.text, 0xfff3dd);
				this.removeIcon();
				this.removeSecondText();
				break;
			case TileToastType.INCOME:
				this.setColor(this.text, 0x19b345);
				this.addIcon(options.iconKey);
				this.removeSecondText();
				break;
			case TileToastType.OFFLINE:
				this.setColor(this.text, 0xf4d278);
				this.addIcon(options.iconKey);
				this.removeSecondText();
				break;
			case TileToastType.EXPLORE:
				this.setColor(this.text, 0xff3d3d);
				this.addIcon(options.iconKey);
				this.addSecondText(options.textTwo || "");
				break;
			case TileToastType.REVEAL:
				this.setColor(this.text, 0x19b345);
				this.addIcon(options.iconKey);
				this.addSecondText(options.textTwo || "");
				break;
			case TileToastType.PROVISION:
				this.setColor(this.text, 0xf4d278);
				this.setBackground(0x004146, 0.7);
				this.removeIcon();
				this.removeSecondText();
				break;
		}

		this.layoutElements();
	}

	setColor(
		obj: Phaser.GameObjects.Text | Phaser.GameObjects.BitmapText,
		hexColor: number,
	): void {
		if ("setTint" in obj) {
			(obj as Phaser.GameObjects.BitmapText).setTint(hexColor);
		} else {
			const hex = `#${hexColor.toString(16).padStart(6, "0")}`;
			(obj as Phaser.GameObjects.Text).setColor(hex);
		}
	}

	addIcon(key?: string): void {
		if (!key) {
			this.removeIcon();
			return;
		}

		let mutatedKey = key;
		if (
			key === "icon-eventium" ||
			key === "icon-christmas-eventium" ||
			key === "icon-easter-eventium" ||
			key === "icon-firefest-eventium" ||
			key === "icon-fishing-eventium" ||
			key === "icon-solarfair-eventium" ||
			key === "icon-hunting-eventium" ||
			key === "icon-halloween-eventium" ||
			key === "icon-pirates-eventium"
		) {
			mutatedKey = resolveSeasonalEventiumIconKey(key);
		}
		if (!this.icon) {
			if (
				mutatedKey === "icon-christmas-eventium" ||
				mutatedKey === "icon-easter-eventium" ||
				mutatedKey === "icon-firefest-eventium" ||
				mutatedKey === "icon-fishing-eventium" ||
				mutatedKey === "icon-solarfair-eventium" ||
				mutatedKey === "icon-hunting-eventium" ||
				mutatedKey === "icon-halloween-eventium" ||
				mutatedKey === "icon-pirates-eventium"
			) {
				this.icon = this.scene.add.image(0, 0, mutatedKey);
			} else {
				this.icon = this.scene.add.sprite(
					0,
					0,
					"exploration-icons",
					spriteKeyToIndex[key as keyof typeof spriteKeyToIndex] || 0,
				);
			}

			this.container.add(this.icon);
		} else {
			this.icon.setTexture(
				mutatedKey.includes("eventium")
					? mutatedKey
					: "exploration-icons",
			);
			if (!mutatedKey.includes("eventium")) {
				this.icon.setFrame(
					spriteKeyToIndex[key as keyof typeof spriteKeyToIndex] || 0,
				);
			}
			this.icon.setVisible(true);
		}
		this.icon.setDisplaySize(120, 120);
	}

	removeIcon(): void {
		if (this.icon) this.icon.setVisible(false);
	}

	addSecondText(text: string): void {
		const correctSpacesText = text.replace(/\u00A0/g, " ");
		if (!this.secondText) {
			this.secondText = this.useBitmapText
				? this.scene.add
						.bitmapText(0, 0, "texturina", correctSpacesText, 112)
						.setOrigin(0.5)
				: this.scene.add
						.text(0, 0, correctSpacesText, {
							fontSize: "7rem",
							color: "#fff3dd",
							align: "center",
							fontFamily: "Texturina",
						})
						.setOrigin(0.5);
			if (this.useBitmapText) {
				this.secondText.setOrigin(0.5, 0.95);
			}
			this.container.add(this.secondText);
		} else {
			this.secondText.setText(correctSpacesText);
			this.secondText.setVisible(true);
		}
	}

	removeSecondText(): void {
		if (this.secondText) this.secondText.setVisible(false);
	}

	layoutElements(): void {
		const padding = 10;
		let totalWidth = 0;

		const iconVisible = this.icon && this.icon.visible;
		const secondTextVisible = this.secondText && this.secondText.visible;

		const mainTextWidth = getWidth(this.text);
		const iconWidth = iconVisible ? this.icon!.displayWidth : 0;
		const secondTextWidth = secondTextVisible
			? getWidth(this.secondText!)
			: 0;

		totalWidth = mainTextWidth;
		if (iconVisible) totalWidth += padding + iconWidth;
		if (secondTextVisible) totalWidth += padding + secondTextWidth;

		let x = -totalWidth / 2;

		this.text.x = x + mainTextWidth / 2;
		x += mainTextWidth;

		if (iconVisible) {
			x += padding;
			this.icon!.x = x + iconWidth / 2;
			x += iconWidth;
		}

		if (secondTextVisible) {
			x += padding;
			this.secondText!.x = x + secondTextWidth / 2;
		}

		const offsetY = this.useBitmapText ? -this.text.height * 0.5 : 0;
		this.text.y = offsetY;

		if (iconVisible) this.icon!.y = 0;
		if (secondTextVisible) this.secondText!.y = 0;

		const bgWidth = totalWidth;
		this.bg.setSize(bgWidth, this.leftDecor.height);
		this.leftDecor.x = -bgWidth / 2 - this.leftDecor.width;
		this.rightDecor.x = bgWidth / 2 + this.leftDecor.width;
	}

	show(
		position: { x: number; y: number },
		cameraZoom: number,
		messageScale: boolean = false,
		ignoreBounds: boolean = false,
	): void {
		const dpr = window.devicePixelRatio > 3 ? 2.6 : window.devicePixelRatio;

		// Kamera a jej viditeľná oblasť vo world coordinates
		const cam = this.scene.cameras.main;
		const view = cam.worldView;

		// Pôvodná veľkosť containeru (bez škálovania)
		const baseWidth =
			this.leftDecor.width + this.bg.width + this.rightDecor.width;

		// Výpočet scale podľa zoomu
		const SCALE_COEFFICIENT = 0.15 * dpr;
		const scaleValue =
			(1 / (cameraZoom ?? 1)) *
			SCALE_COEFFICIENT *
			(messageScale ? 1.2 : 1);

		// Veľkosť toastu po scale
		const scaledWidth = baseWidth * scaleValue;

		// Korekcia pozície, aby toast bol celý vo vnútri kamery
		let x = position.x;
		let y = position.y;

		const headerElement = document.getElementById("header-wrapper");
		const headerHeight = headerElement ? headerElement.offsetHeight : 0;

		if (!ignoreBounds) {
			if (y < view.y + headerHeight * (1 / (cameraZoom ?? 1))) {
				y =
					view.y +
					this.bg.height * 2 +
					headerHeight * (1 / (cameraZoom ?? 1));
			}

			//  Kontrola X - nesmie vyjsť mimo camera worldView
			if (x - scaledWidth / 2 < view.x) x = view.x + scaledWidth / 2;

			if (x + scaledWidth / 2 > view.x + view.width)
				x = view.x + view.width - scaledWidth / 2;
		}

		// Nastav pozíciu s korekciou na zoom
		this.container.setPosition(x, y);
		this.container.setAlpha(0.85);
		this.container.setVisible(true);
		this.container.setScale(scaleValue * 0.1);

		this.scene.tweens.add({
			targets: this.container,
			alpha: 1,
			duration: 300,
			scale: scaleValue,
			ease: "Quad.easeOut",
		});
	}

	hideAnimated(
		onComplete?: () => void,
		duration: number = 1500,
		noAnim = false,
		scaleValue = 1,
	): void {
		this.cancelTween();
		this.currentTween = this.scene.tweens.add({
			targets: this.container,
			y: this.container.y - (noAnim ? 0 : 300 * scaleValue),
			alpha: 1,
			duration: duration,
			ease: "Quad.easeOut",
		});
		this.scene.time.delayedCall(duration, () => {
			this.cancelTween();
			this.currentTween = this.scene.tweens.add({
				targets: this.container,
				alpha: 0,
				duration: 300,
				ease: "Quad.easeOut",
				onComplete: (): void => {
					this.container.setVisible(false);
					this.container.setAlpha(0);
					this.container.setScale(1);
					this.activeVariant = undefined;
					this.currentTween = undefined;
					if (onComplete) onComplete();
				},
			});
		});
	}

	cancelTween(): void {
		if (this.currentTween) {
			this.currentTween.stop();
			this.currentTween = undefined;
		}
	}

	hide(): void {
		this.cancelTween();
		this.container.setVisible(false);
		this.container.setAlpha(0);
		this.activeVariant = undefined;
	}
}
